<template>
  <v-dialog :retain-focus="false" :value="sectionEditor.show" :width="1000" @input="handleClosePopup">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
          <span style="color: #0081c7">{{ fields.title }}</span>
          <v-btn icon @click="handleClosePopup">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
      </div>

      <v-card-text style="max-height: 70vh; overflow-y: auto">
        <!-- General Section Settings -->
        <div class="mb-4">
          <label class="label-input">Section Settings</label>
          <v-row>
            <v-col cols="12" md="8">
              <v-text-field v-model="fields.title" label="Section Name" hide-details />
            </v-col>
            <v-col cols="12" md="4">
              <v-checkbox v-model="fields.enableAnchor" label="Enable Anchor Link" hide-details />
            </v-col>
          </v-row>
        </div>

        <div class="mb-4">
          <SectionSettingsContainerFuild
            :use-background-mobile="true"
            :settings="fields.settings"
            :container-page="fields.containerPage"
          />
        </div>

        <!-- Global Tab Settings -->
        <div class="mb-4">
          <label class="label-input">Tabs Styling Settings</label>
          <div class="group-item">
            <v-row>
              <v-col cols="12" md="6">
                <div class="d-flex align-center gap-3">
                  <input
                    type="color"
                    style="width: 40px; height: 40px; margin-right: 12px"
                    :value="fields.colorBorder"
                    @change="e => (fields.colorBorder = e.target.value)"
                  />
                  <v-text-field v-model="fields.colorBorder" label="Border Active Color" hide-details />
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="fields.widthTabContentItem"
                  type="number"
                  label="Content Item Width"
                  suffix="px"
                  hide-details
                />
              </v-col>
            </v-row>
          </div>
        </div>

        <!-- Tabs List -->
        <div class="mb-4">
          <div class="d-flex justify-space-between align-center">
            <label class="label-input mb-0">Tabs Configuration</label>
            <v-btn small text color="primary" @click="handleAddTab">
              <v-icon size="16">mdi-plus</v-icon>
              <span>Add Tab</span>
            </v-btn>
          </div>
          <div class="group-item">
            <v-expansion-panels accordion>
              <v-expansion-panel v-for="(tab, index) in fields.listTab" :key="`tab-${index}`">
                <v-expansion-panel-header>
                  <div class="d-flex justify-space-between align-center">
                    <span>Tab {{ index + 1 }}: {{ tab.headerText || 'Untitled Tab' }}</span>
                    <v-btn icon :disabled="fields.listTab.length <= 1" @click.stop="handleRemoveTab(index)">
                      <v-icon size="18" color="#ff6c6c">mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <!-- Tab Header Text -->
                  <v-text-field v-model="tab.headerText" label="Tab Label Text" class="mb-3" />

                  <!-- Tab Background Settings -->
                  <div class="mb-3">
                    <label class="d-block mb-1">Desktop Background Image</label>
                    <UploadImage
                      @callback="obj => handleUploadTabBgDesktop(obj, index)"
                      :image="{ imageSrc: tab.bgDesktopUrl || '' }"
                      :height="`160`"
                      :typeUpload="'image'"
                      @callbackDelete="() => handleRemoveTabBgDesktop(index)"
                    />
                  </div>
                  <div class="mb-3">
                    <label class="d-block mb-1">Mobile Background Image</label>
                    <UploadImage
                      @callback="obj => handleUploadTabBgMobile(obj, index)"
                      :image="{ imageSrc: tab.bgMobileUrl || '' }"
                      :height="`160`"
                      :typeUpload="'image'"
                      @callbackDelete="() => handleRemoveTabBgMobile(index)"
                    />
                  </div>
                  <v-checkbox v-model="tab.useBgMobile" label="Use Mobile Background Image" class="mb-3" />

                  <!-- Tab Subtitle -->
                  <div class="mb-3">
                    <div class="d-flex align-center justify-space-between mb-2">
                      <label class="mb-0">Subtitle Content</label>
                      <v-checkbox v-model="tab.subTitleShow" label="Show Subtitle" hide-details class="mt-0" />
                    </div>
                    <ckeditor :editor="editor" v-model="tab.subTitleText" :config="editorConfig" />
                  </div>

                  <!-- Tab Title/Description Content -->
                  <div class="mb-3">
                    <label class="mb-2 d-block">Main Tab Content (Title & Description)</label>
                    <ckeditor :editor="editor" v-model="tab.titleContent" :config="editorConfig" />
                  </div>

                  <!-- Benefits Section inside Tab -->
                  <div class="mb-3">
                    <div class="d-flex align-center justify-space-between mb-2">
                      <label class="mb-0">Benefits Block</label>
                      <v-checkbox v-model="tab.benefitsShow" label="Show Benefits" hide-details class="mt-0" />
                    </div>
                    <div v-if="tab.benefitsShow" class="group-item pl-3 pr-3 pt-3">
                      <v-row class="mb-2">
                        <v-col cols="12">
                          <div class="d-flex align-center gap-3">
                            <input
                              type="color"
                              style="width: 36px; height: 36px; margin-right: 8px"
                              :value="tab.colorBenefit"
                              @change="e => (tab.colorBenefit = e.target.value)"
                            />
                            <v-text-field
                              v-model="tab.colorBenefit"
                              label="Benefit Icon Border/Text Color"
                              dense
                              hide-details
                            />
                          </div>
                        </v-col>
                      </v-row>

                      <div class="d-flex justify-space-between align-center mb-2">
                        <span class="font-weight-bold">Benefit Items</span>
                        <v-btn small text color="primary" @click="handleAddBenefitItem(index)">
                          <v-icon size="14">mdi-plus</v-icon> Add Benefit
                        </v-btn>
                      </div>

                      <v-expansion-panels accordion class="mb-2">
                        <v-expansion-panel
                          v-for="(benefit, bIndex) in tab.benefits"
                          :key="`benefit-${index}-${bIndex}`"
                        >
                          <v-expansion-panel-header>
                            <div class="d-flex justify-space-between align-center">
                              <span>Benefit {{ bIndex + 1 }}: {{ benefit.text || 'Untitled' }}</span>
                              <v-btn icon @click.stop="handleRemoveBenefitItem(index, bIndex)">
                                <v-icon size="16" color="#ff6c6c">mdi-delete</v-icon>
                              </v-btn>
                            </div>
                          </v-expansion-panel-header>
                          <v-expansion-panel-content>
                            <div class="mb-3">
                              <label class="d-block mb-1">Benefit Icon Image</label>
                              <UploadImage
                                @callback="obj => handleUploadBenefitIcon(obj, index, bIndex)"
                                :image="{ imageSrc: benefit.icon || '' }"
                                :height="`100`"
                                :typeUpload="'image'"
                                @callbackDelete="() => handleRemoveBenefitIcon(index, bIndex)"
                              />
                            </div>
                            <v-text-field v-model="benefit.text" label="Benefit Text" />
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                      </v-expansion-panels>
                    </div>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </div>
      </v-card-text>

      <div class="footer-modal">
        <v-card-actions style="padding: 10px 0">
          <v-spacer></v-spacer>
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import UploadImage from '@/components/form/uploadFile/UpdateImage.vue';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { updateHtmlFromData } from '../common/html/update';
import SectionSettingsContainerFuild from './common/section-settings-container-fuild.vue';

export default {
  name: 'card-tab-with-content-losaprily-section-editor',
  components: {
    UploadImage,
    SectionSettingsContainerFuild,
  },
  props: {
    sectionEditor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      fields: {
        title: 'Card Tab With Content Losaprily Section',
        enableAnchor: false,
        settings: {
          paddingTop: true,
          paddingBottom: true,
          backgroundColor: '#FFFFFF',
          textColor: '#000000',
          overlayColor: '#000000',
          overlayOpacity: 0,
          showOverlay: false,
          backgroundImage: '',
        },
        containerPage: {
          enableStyle: false,
          background: '#FFFFFF',
        },
        colorBorder: '#fe7b01',
        widthTabContentItem: 576,
        listTab: [],
      },
    };
  },
  mounted() {
    this.applyExtract(this.sectionEditor.sectionExtract);
  },
  watch: {
    sectionEditor: {
      handler(newVal) {
        if (newVal && newVal.sectionExtract) {
          this.applyExtract(newVal.sectionExtract);
        }
      },
      deep: true,
    },
  },
  methods: {
    applyExtract(extract) {
      if (!extract) return;
      this.fields = {
        ...this.fields,
        ...extract,
        settings: { ...this.fields.settings, ...(extract.settings || {}) },
        containerPage: { ...this.fields.containerPage, ...(extract.containerPage || {}) },
        listTab:
          Array.isArray(extract.listTab) && extract.listTab.length
            ? JSON.parse(JSON.stringify(extract.listTab))
            : this.getDefaultTabs(),
      };
    },
    getDefaultTabs() {
      return [
        {
          tabId: 'tab-1',
          headerText: 'Stabil talp',
          bgDesktopUrl: 'https://cdn.shopify.com/s/files/1/0623/9653/0827/files/b1510c0d-image-13.jpg',
          bgMobileUrl: 'https://cdn.shopify.com/s/files/1/0623/9653/0827/files/b1510c0d-image-12.jpg',
          useBgMobile: true,
          subTitleShow: true,
          subTitleText: '<h4>Felborulás elleni védelem</h4>',
          titleContent:
            '<h2>STABIL, MINT EGY REPÜLŐGÉP SZÁRNYA</h2><p>Az extra széles szárnyak và háromszög alakú talp megakadályozzák a felborulást, így gyermeked mindig biztonságban van.</p>',
          colorBenefit: '#0079eb',
          benefitsShow: true,
          benefits: [
            {
              icon: 'https://cdn.skyfastimg.com/images/2026/06/25/image-6a3c9d138dad108f48397a16.png',
              text: 'Kiszélesített oldalszárnyak',
            },
            {
              icon: 'https://cdn.skyfastimg.com/images/2026/06/25/image-6a3c9d138dad108f48397a16.png',
              text: 'Háromszög alakú, stabil talp',
            },
            {
              icon: 'https://cdn.skyfastimg.com/images/2026/06/25/image-6a3c9d138dad108f48397a16.png',
              text: 'Felborulásgátló védőrendszer',
            },
          ],
        },
      ];
    },
    handleClosePopup() {
      this.$emit('close');
    },
    handleSave() {
      const updateHtml = updateHtmlFromData.sectionCardTabWithContentLosaprily(
        this.sectionEditor.section.html,
        this.fields,
      );
      this.$emit('update:section', {
        ...this.sectionEditor.section,
        title: this.fields.title,
        html: updateHtml,
      });
      this.$emit('close');
    },
    handleAddTab() {
      const newIndex = this.fields.listTab.length + 1;
      this.fields.listTab.push({
        tabId: `tab-${newIndex}`,
        headerText: `New Tab ${newIndex}`,
        bgDesktopUrl: 'https://cdn.shopify.com/s/files/1/0623/9653/0827/files/b1510c0d-image-13.jpg',
        bgMobileUrl: 'https://cdn.shopify.com/s/files/1/0623/9653/0827/files/b1510c0d-image-12.jpg',
        useBgMobile: true,
        subTitleShow: true,
        subTitleText: '<h4>Subtitle Text</h4>',
        titleContent: '<h2>Tab Heading Title</h2><p>Tab content description paragraph.</p>',
        colorBenefit: '#0079eb',
        benefitsShow: true,
        benefits: [
          {
            icon: 'https://cdn.skyfastimg.com/images/2026/06/25/image-6a3c9d138dad108f48397a16.png',
            text: 'Benefit item 1',
          },
        ],
      });
    },
    handleRemoveTab(index) {
      if (this.fields.listTab.length <= 1) return;
      this.fields.listTab.splice(index, 1);
      // Re-index tabIds
      this.fields.listTab.forEach((tab, i) => {
        tab.tabId = `tab-${i + 1}`;
      });
    },
    handleUploadTabBgDesktop(obj, index) {
      if (!this.fields.listTab[index]) return;
      this.$set(this.fields.listTab[index], 'bgDesktopUrl', obj.imageSrc || '');
    },
    handleRemoveTabBgDesktop(index) {
      if (!this.fields.listTab[index]) return;
      this.$set(this.fields.listTab[index], 'bgDesktopUrl', '');
    },
    handleUploadTabBgMobile(obj, index) {
      if (!this.fields.listTab[index]) return;
      this.$set(this.fields.listTab[index], 'bgMobileUrl', obj.imageSrc || '');
    },
    handleRemoveTabBgMobile(index) {
      if (!this.fields.listTab[index]) return;
      this.$set(this.fields.listTab[index], 'bgMobileUrl', '');
    },
    handleAddBenefitItem(tabIndex) {
      if (!this.fields.listTab[tabIndex]) return;
      if (!this.fields.listTab[tabIndex].benefits) {
        this.$set(this.fields.listTab[tabIndex], 'benefits', []);
      }
      this.fields.listTab[tabIndex].benefits.push({
        icon: 'https://cdn.skyfastimg.com/images/2026/06/25/image-6a3c9d138dad108f48397a16.png',
        text: 'New benefit item text',
      });
    },
    handleRemoveBenefitItem(tabIndex, benefitIndex) {
      if (!this.fields.listTab[tabIndex] || !this.fields.listTab[tabIndex].benefits) return;
      this.fields.listTab[tabIndex].benefits.splice(benefitIndex, 1);
    },
    handleUploadBenefitIcon(obj, tabIndex, benefitIndex) {
      if (!this.fields.listTab[tabIndex] || !this.fields.listTab[tabIndex].benefits[benefitIndex]) return;
      this.$set(this.fields.listTab[tabIndex].benefits[benefitIndex], 'icon', obj.imageSrc || '');
    },
    handleRemoveBenefitIcon(tabIndex, benefitIndex) {
      if (!this.fields.listTab[tabIndex] || !this.fields.listTab[tabIndex].benefits[benefitIndex]) return;
      this.$set(this.fields.listTab[tabIndex].benefits[benefitIndex], 'icon', '');
    },
  },
};
</script>

<style scoped lang="scss">
.group-item {
  padding: 15px 10px;
  border-radius: 10px;
  border: 1px solid #e0e0e0;
}
.label-input {
  font-size: 18px !important;
  font-weight: 500;
  color: #000;
  margin-bottom: 10px;
  display: block;
}
.header-modal {
  position: sticky;
  top: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  padding: 0 16px;
  box-shadow: 0px 0px 12px #0000000f;
}
.footer-modal {
  position: sticky;
  padding: 0 16px;
  bottom: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  box-shadow: 0px 0px 12px #0000000f;
}
</style>
